// @flow

import * as React from 'react';
import { compose, withState, withHandlers } from 'recompose';
import { connect } from 'react-redux';
import Radium from 'radium';
import DateTimeField from 'react-datetime';

import {
  Card,
  CardHeader,
  CardBlock,
  FormGroup,
  Button,
  Label,
  Input,
  Alert,
} from 'reactstrap';

import ImageUpload from 'components/ImageUpload';

import { createAppointment, loadAppointments } from 'actions/appointments';

import { Spacing } from 'utils/styleguide';

type Props = {
  // recompose
  title: string,
  onTitleChange: (title: string) => void,
  description: string,
  onDescriptionChange: (description: string) => void,
  image: ?string,
  onImageChange: (image: ?string) => void,
  dates: Array<number>,
  onDatesChange: (dates: Array<number>) => void,
  addDate: (newDate: number) => void,
  changeDate: (newDate: number, index: number) => void,
  removeDate: (index: number) => void,

  // redux
  error: ?string,
  onCreateAppointment: typeof createAppointment,
  onLoadAppointments: typeof loadAppointments,
};

class NewAppointment extends React.Component<Props> {
  _onSubmitAppointment = async () => {
    const {
      title,
      description,
      dates,
      image,
      onCreateAppointment,
      onTitleChange,
      onDescriptionChange,
      onDatesChange,
      onImageChange,
      onLoadAppointments,
    } = this.props;

    try {
      await onCreateAppointment(title, description, dates, image);
      await onLoadAppointments();

      onTitleChange('');
      onDescriptionChange('');
      onDatesChange([+new Date()]);
      onImageChange(null);
    } catch (e) {
      console.error(e);
    }
  };

  render() {
    const {
      title,
      onTitleChange,
      description,
      onDescriptionChange,
      image,
      onImageChange,
      error,
      dates,
      addDate,
      changeDate,
      removeDate,
    } = this.props;

    return (
      <Card style={styles.root}>
        <CardHeader style={styles.title}>Neuen Termin erstellen</CardHeader>
        <CardBlock>
          <FormGroup>
            <Input
              type="textfield"
              placeholder="Titel des Termins ..."
              value={title}
              onChange={event => onTitleChange(event.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Input
              type="textarea"
              placeholder="Beschreibung des Termins ..."
              value={description}
              onChange={event => onDescriptionChange(event.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Daten auswählen</Label>
            {dates.map((date, index) => {
              return (
                <div key={`${date}-${index}`} style={styles.dateRow}>
                  <div style={styles.dateInput}>
                    <DateTimeField
                      defaultValue={new Date(date)}
                      onBlur={moment => changeDate(moment.unix() * 1000, index)}
                    />
                  </div>
                  <Button
                    color="danger"
                    size="xs"
                    onClick={() => removeDate(index)}
                  >
                    x
                  </Button>
                </div>
              );
            })}
            <Button
              color="link"
              style={styles.link}
              size="sm"
              onClick={() => addDate(dates[dates.length - 1] || +new Date())}
            >
              Weiteren Termin hinzufügen
            </Button>
          </FormGroup>
          <FormGroup>
            <Label>Bild hinzufügen</Label>
            <ImageUpload image={image} onImageSelected={onImageChange} />
          </FormGroup>
          {!!error && (
            <Alert color="danger">
              {error}: Konnte Termin nicht anlegen. Bitte Support kontaktieren.
            </Alert>
          )}
          <Button
            color="primary"
            disabled={!title || !description || dates.length === 0}
            onClick={this._onSubmitAppointment}
          >
            Termin erstellen
          </Button>
        </CardBlock>
      </Card>
    );
  }
}

const styles = {
  root: {
    width: '100%',
  },
  title: {
    fontWeight: 'bold',
  },
  link: {
    fontSize: 12,
    textAlign: 'center',
    cursor: 'pointer',
  },
  images: {
    display: 'flex',
  },
  dateRow: {
    display: 'flex',
    marginBottom: Spacing.Nano,
  },
  dateInput: {
    flexGrow: 1,
    marginRight: Spacing.Nano,
  },
};

const mapStateToProps = ({ appointments: { error } }) => ({
  error: (error && error.code) || null,
});
const mapDispatchToProps = dispatch => ({
  onCreateAppointment: (...args) => dispatch(createAppointment(...args)),
  onLoadAppointments: (...args) => dispatch(loadAppointments(...args)),
});

const NewAppointmentContainer = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withState('title', 'onTitleChange', ''),
  withState('description', 'onDescriptionChange', ''),
  withState('images', 'onImagesChange', {}),
  withState('dates', 'onDatesChange', [+new Date()]),
  withState('image', 'onImageChange', null),
  withHandlers({
    addDate: ({ dates, onDatesChange }) => newDate => {
      onDatesChange([...dates, newDate]);
    },
    changeDate: ({ dates, onDatesChange }) => (newDate, index) => {
      if (index < 0 || index > dates.length - 1) {
        return;
      }
      onDatesChange([
        ...dates.slice(0, index),
        newDate,
        ...dates.slice(index + 1),
      ]);
    },
    removeDate: ({ dates, onDatesChange }) => index => {
      if (index < 0 || index > dates.length - 1) {
        return;
      }
      onDatesChange([...dates.slice(0, index), ...dates.slice(index + 1)]);
    },
  }),
  Radium
)(NewAppointment);

export default NewAppointmentContainer;



// WEBPACK FOOTER //
// ./src/views/Appointments/NewAppointment.js